import React, { useEffect, useRef } from 'react'

interface ParallaxProps {
  children: React.ReactNode
  /** Scroll-drift factor: translate = (elementCenter - viewportCenter) * factor. 0.08 ≈ ±8% drift. */
  factor?: number
  axis?: 'y' | 'x'
  /** Max absolute translation in px */
  max?: number
  className?: string
}

/**
 * Continuous scroll-linked drift (Parity V2 A.3).
 * rAF-throttled, transform-only. Disabled under prefers-reduced-motion;
 * runs at 40% strength on viewports < lg (perf).
 */
const Parallax: React.FC<ParallaxProps> = ({
  children,
  factor = 0.08,
  axis = 'y',
  max,
  className = '',
}) => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    // Light mobile motion (Polish Plan item 9): reduced factor below lg instead of full bail
    const scale = window.innerWidth < 1024 ? 0.4 : 1
    const cap = max !== undefined ? max * scale : undefined

    let raf = 0
    const update = () => {
      raf = 0
      const rect = el.getBoundingClientRect()
      const delta = rect.top + rect.height / 2 - window.innerHeight / 2
      let offset = -delta * factor * scale
      if (cap !== undefined) offset = Math.max(-cap, Math.min(cap, offset))
      el.style.transform =
        axis === 'y' ? `translate3d(0, ${offset}px, 0)` : `translate3d(${offset}px, 0, 0)`
    }
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update)
    }
    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      if (raf) cancelAnimationFrame(raf)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [factor, axis, max])

  return (
    <div ref={ref} className={className} style={{ willChange: 'transform' }}>
      {children}
    </div>
  )
}

export default Parallax
